import React, { useState, useEffect, useCallback } from 'react';
import { getAllRegionalUsers, createRegionalUser, updateRegionalUser, deleteRegionalUser, updateRegionalUserPassword, assignRegions } from '../../services/regionalUserService';
import { Modal, message } from 'antd';
import { FaPlus, FaCheck, FaUserFriends } from 'react-icons/fa';
import api from '../../services/api';
import './SportsManagementStyle.scss';
import RegionalUserListTable from './RegionalUserListTable';

const RegionalUsersManagement = () => {
    const [users, setUsers] = useState([]);
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showRegionsModal, setShowRegionsModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        assignedRegionIds: [],
        isActive: true
    });
    const [newPassword, setNewPassword] = useState('');
    const [selectedUserRegions, setSelectedUserRegions] = useState([]);

    useEffect(() => {
        loadUsers();
        loadRegions();
    }, []);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const res = await getAllRegionalUsers();
            if (res?.isSuccess) {
                setUsers(res.data);
            }
        } catch (error) {
            message.error('Kullanıcılar yüklenemedi.');
        } finally {
            setLoading(false);
        }
    };

    const loadRegions = async () => {
        try {
            const res = await api.get('/api/sports/regions-with-facilities');
            if (res?.data) {
                setRegions(res.data);
            }
        } catch (error) {
            console.error('Bölgeler yüklenemedi:', error);
        }
    };

    // Memoized Handlers for Table
    const handleOpenModal = useCallback((user = null) => {
        if (user) {
            setEditingUser(user);
            setFormData({
                username: user.username,
                password: '',
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                assignedRegionIds: user.assignedRegionIds || [],
                isActive: user.isActive
            });
        } else {
            setEditingUser(null);
            setFormData({
                username: '',
                password: '',
                firstName: '',
                lastName: '',
                email: '',
                assignedRegionIds: [],
                isActive: true
            });
        }
        setShowModal(true);
    }, []);

    const handleOpenPasswordModal = useCallback((user) => {
        setEditingUser(user);
        setNewPassword('');
        setShowPasswordModal(true);
    }, []);

    const handleOpenRegionsModal = useCallback((user) => {
        setEditingUser(user);
        setSelectedUserRegions(user.assignedRegionIds || []);
        setShowRegionsModal(true);
    }, []);

    const handleDelete = useCallback(async (id) => {
        Modal.confirm({
            title: 'Onay',
            content: 'Bu kullanıcıyı silmek istediğinize emin misiniz?',
            okText: 'Evet',
            cancelText: 'Hayır',
            onOk: async () => {
                try {
                    await deleteRegionalUser(id);
                    message.success('Kullanıcı silindi.');
                    // Optimistic Update
                    setUsers(prev => prev.filter(u => u.id !== id));
                } catch (error) {
                    message.error('Silme işlemi başarısız.');
                }
            }
        });
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (editingUser) {
                // Update
                await updateRegionalUser(editingUser.id, {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    isActive: formData.isActive
                });
                // Update regions (if handled here?) 
                // Note: Original code called assignRegions here too, keeping logic same.
                await assignRegions(editingUser.id, formData.assignedRegionIds);
                message.success('Kullanıcı güncellendi.');
            } else {
                // Create
                await createRegionalUser(formData);
                message.success('Kullanıcı oluşturuldu.');
            }
            setShowModal(false);
            loadUsers();
        } catch (error) {
            message.error('İşlem başarısız.');
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        if (!newPassword || newPassword.length < 6) {
            message.error('Şifre en az 6 karakter olmalıdır.');
            return;
        }
        try {
            await updateRegionalUserPassword(editingUser.id, newPassword);
            message.success('Şifre güncellendi.');
            setShowPasswordModal(false);
        } catch (error) {
            message.error('Şifre güncellenemedi.');
        }
    };

    const handleSaveRegions = async (e) => {
        e.preventDefault();
        try {
            await assignRegions(editingUser.id, selectedUserRegions);
            message.success('Bölge atamaları güncellendi.');
            setShowRegionsModal(false);
            loadUsers();
        } catch (error) {
            message.error('Atama güncellenemedi.');
        }
    };

    const toggleRegion = (regionId) => {
        setSelectedUserRegions(prev =>
            prev.includes(regionId)
                ? prev.filter(id => id !== regionId)
                : [...prev, regionId]
        );
    };

    return (
        <div className="sports-management-page">
            <div className="page-header">
                <h1>Bölge Kullanıcıları Yönetimi</h1>
                <button
                    className="btn-premium"
                    onClick={() => handleOpenModal()}
                >
                    <FaPlus /> Yeni Kullanıcı
                </button>
            </div>

            <div className="premium-card">
                <div className="card-header">
                    <h3>Kullanıcılar</h3>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="loading-spinner">Yükleniyor...</div>
                    ) : (
                        <RegionalUserListTable
                            users={users}
                            handleOpenModal={handleOpenModal}
                            handleOpenPasswordModal={handleOpenPasswordModal}
                            handleOpenRegionsModal={handleOpenRegionsModal}
                            handleDelete={handleDelete}
                        />
                    )}
                </div>
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>{editingUser ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı Ekle'}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="modal-body">
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    {!editingUser && (
                                        <>
                                            <div className="form-group">
                                                <label>Kullanıcı Adı</label>
                                                <input
                                                    type="text"
                                                    required
                                                    value={formData.username}
                                                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label>Şifre</label>
                                                <input
                                                    type="password"
                                                    required
                                                    value={formData.password}
                                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div className="form-group">
                                        <label>Ad</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.firstName}
                                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Soyad</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.lastName}
                                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>E-posta</label>
                                    <input
                                        type="email"
                                        required
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Durum</label>
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal' }}>
                                            <input
                                                type="radio"
                                                checked={formData.isActive}
                                                onChange={() => setFormData({ ...formData, isActive: true })}
                                            /> Aktif
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'normal' }}>
                                            <input
                                                type="radio"
                                                checked={!formData.isActive}
                                                onChange={() => setFormData({ ...formData, isActive: false })}
                                            /> Pasif
                                        </label>
                                    </div>
                                </div>

                                {!editingUser && (
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Bölge Atamaları (Sadece Bir Bölge Seçiniz)</label>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                            gap: '10px',
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            padding: '8px'
                                        }}>
                                            {regions.map(region => {
                                                const isSelected = formData.assignedRegionIds.includes(region.id);
                                                return (
                                                    <label key={region.id} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '8px',
                                                        padding: '12px',
                                                        border: `2px solid ${isSelected ? '#4e73df' : '#e3e6f0'}`,
                                                        borderRadius: '8px',
                                                        backgroundColor: isSelected ? '#f8f9fc' : '#ffffff',
                                                        cursor: 'pointer',
                                                        fontWeight: isSelected ? 'bold' : 'normal',
                                                        color: isSelected ? '#4e73df' : '#5a5c69',
                                                        transition: 'all 0.2s',
                                                        textAlign: 'center'
                                                    }}>
                                                        <input
                                                            type="radio"
                                                            name="regionSelection"
                                                            style={{ display: 'none' }}
                                                            checked={isSelected}
                                                            onChange={() => {
                                                                setFormData({ ...formData, assignedRegionIds: [region.id] });
                                                            }}
                                                        />
                                                        {region.name}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-premium btn-outline" onClick={() => setShowModal(false)}>İptal</button>
                                <button type="submit" className="btn-premium btn-success">
                                    <FaCheck /> {editingUser ? 'Güncelle' : 'Oluştur'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Password Modal */}
            {showPasswordModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px' }}>
                        <div className="modal-header">
                            <h2>Şifre Değiştir</h2>
                            <button className="close-btn" onClick={() => setShowPasswordModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleUpdatePassword}>
                            <div className="modal-body">
                                <p style={{ marginBottom: '1rem' }}><strong>Kullanıcı:</strong> {editingUser?.username}</p>
                                <div className="form-group">
                                    <label>Yeni Şifre</label>
                                    <input
                                        type="password"
                                        required
                                        placeholder="En az 6 karakter"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-premium btn-outline" onClick={() => setShowPasswordModal(false)}>İptal</button>
                                <button type="submit" className="btn-premium btn-success">
                                    <FaCheck /> Güncelle
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Regions Modal */}
            {showRegionsModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2>Bölge Atamaları</h2>
                            <button className="close-btn" onClick={() => setShowRegionsModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSaveRegions}>
                            <div className="modal-body">
                                <p style={{ marginBottom: '1rem' }}><strong>Kullanıcı:</strong> {editingUser?.username}</p>
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label>Atanacak Bölgeyi Seçiniz (Sadece Bir Bölge Seçiniz):</label>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                        gap: '10px',
                                        marginTop: '10px',
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        padding: '4px'
                                    }}>
                                        {regions.map(region => {
                                            const isSelected = selectedUserRegions.includes(region.id);
                                            return (
                                                <label key={region.id} style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    padding: '12px',
                                                    border: `2px solid ${isSelected ? '#4e73df' : '#e3e6f0'}`,
                                                    borderRadius: '8px',
                                                    backgroundColor: isSelected ? '#f8f9fc' : '#ffffff',
                                                    cursor: 'pointer',
                                                    fontWeight: isSelected ? 'bold' : 'normal',
                                                    color: isSelected ? '#4e73df' : '#5a5c69',
                                                    transition: 'all 0.2s',
                                                    textAlign: 'center'
                                                }}>
                                                    <input
                                                        type="radio"
                                                        name="editRegionSelection"
                                                        style={{ display: 'none' }}
                                                        checked={isSelected}
                                                        onChange={() => setSelectedUserRegions([region.id])}
                                                    />
                                                    {region.name}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-premium btn-outline" onClick={() => setShowRegionsModal(false)}>İptal</button>
                                <button type="submit" className="btn-premium btn-success">
                                    <FaCheck /> Kaydet
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegionalUsersManagement;
